import React from 'react';
import '../../App.scss';
import { updateState, loadingStart, loadingComplete, upsertStateIn } from "../StateHelper";
import { Button, Accordion, Pagination, Dropdown } from 'react-bootstrap';
import AuthorizedPage from '../../component/AuthorizedPage';
import OrderApi from '../../api/OrderApi';
import OrderQueryFilter from '../../component/OrderQueryFilter';
import { OrderItemAction } from '../../api/dto/OrderItemAction';
import { OrderAction } from '../../api/dto/OrderAction';
import OrderActionApi from '../../api/OrderActionApi';
import { Role } from '../../domain/Role';

// Componentler
import { OrderAccordionItem } from '../../component/order/OrderAccordionItem';
import { PartiallyApproveModal } from '../../component/order/PartiallyApproveModal';
import LogoXmlImportModal from '../../component/order/LogoXmlImportModal';
import OrderDeleteModal from '../../component/order/OrderDeleteModal';

interface AdminOrderManagementState {
    orders: any[];
    groupedOrders: any[];
    productLiveStockMap: { [key: number]: any };
    selectedOrder: any;
    selectedOrderItem: any;
    selectedOrderId: any;
    selectedOrderItemId: any;
    query: any;
    showApprovePartiallyModal: boolean;
    viewType: "FLAT" | "GROUPED";
    isLoading: boolean;
    // 🔥 SAYFALAMA STATE'LERİ
    page: number;
    size: number;
    totalPages: number;
    showLogoXmlImportModal: boolean;
    showOrderDeleteModal: boolean;
}

const initialState: AdminOrderManagementState = {
    orders: [],
    groupedOrders: [],
    productLiveStockMap: {},
    selectedOrder: null,
    selectedOrderItem: null,
    selectedOrderId: null,
    selectedOrderItemId: null,
    query: null,
    showApprovePartiallyModal: false,
    viewType: "FLAT",
    isLoading: false,
    // 🔥 BAŞLANGIÇ DEĞERLERİ
    page: 0,
    size: 20,
    totalPages: 1,
    showLogoXmlImportModal: false,
    showOrderDeleteModal: false,
};

class AdminOrderManagement extends React.Component<any, AdminOrderManagementState> {

    constructor(props: any) {
        super(props);
        this.state = initialState;
        this.onParciallyApprove = this.onParciallyApprove.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
    }

    // ============================================================
    // 1. STOK HAVUZU YÖNETİMİ
    // ============================================================
    syncStocksFromData(data: any, type: "FLAT" | "GROUPED") {
        let newStockMap = { ...this.state.productLiveStockMap };

        const extractFromOrder = (order: any) => {
            if (!order.orderItems) return;
            order.orderItems.forEach((item: any) => {
                if (item.productId && item.liveStockInfo) {
                    newStockMap[item.productId] = item.liveStockInfo;
                }
            });
        };

        if (type === "FLAT" && Array.isArray(data)) {
            data.forEach((order: any) => extractFromOrder(order));
        }
        else if (type === "GROUPED" && Array.isArray(data)) {
            data.forEach((buyer: any) => {
                if (buyer.warehouses) {
                    buyer.warehouses.forEach((wh: any) => {
                        if (wh.orders) wh.orders.forEach((order: any) => extractFromOrder(order));
                    });
                }
            });
        }

        this.setState({ productLiveStockMap: newStockMap });
    }

    mergeStockUpdates(stockUpdates: any) {
        if (!stockUpdates || Object.keys(stockUpdates).length === 0) return;

        this.setState(prevState => {
            const currentMap = { ...prevState.productLiveStockMap };
            const newMap = { ...currentMap, ...stockUpdates };
            return { productLiveStockMap: newMap };
        });
    }

    // ============================================================
    // 2. API VE STATE YÖNETİMİ
    // ============================================================
    refresh() {
        loadingStart(this);
        let query = this.state.query;
        this.setState(initialState);
        updateState(this, "query", query);
        loadingComplete(this);
    }

    // 🔥 SAYFA DEĞİŞTİRME METODU
    handlePageChange(newPage: number) {
        if (newPage >= 0 && newPage < this.state.totalPages) {
            this.setState({ page: newPage }, () => {
                this.loadOrders(this.state.query, newPage);
            });
        }
    }

    // 🔥 API ÇAĞRISI (SAYFALAMA EKLENDİ)
    async loadOrders(query: any, page: number = this.state.page) {
        loadingStart(this);

        // Sorguya sayfalama parametrelerini zorunlu ekliyoruz
        const finalQuery = { ...query, page: page, size: this.state.size };

        if (this.state.viewType === "FLAT") {
            // 🔥 YENİ YAZDIĞINIZ ENDPOINT KULLANILIYOR
            let response = await OrderApi.listByQueryPaged(finalQuery);
            if (response.success) {
                // Spring Page objesinden verileri al (content = liste verisi, number = sayfa no)
                const pageData = response.data;
                const contentList = pageData.content || [];

                this.setState({
                    orders: contentList,
                    page: pageData.number !== undefined ? pageData.number : 0,
                    totalPages: pageData.totalPages !== undefined ? pageData.totalPages : 1
                });

                this.syncStocksFromData(contentList, "FLAT");
            }
        }

        if (this.state.viewType === "GROUPED") {
            // Müşteri bazlı görünüm (Sayfalamasız)
            let response = await OrderApi.filterGroupedByBuyerAndWarehouse(finalQuery);
            if (response.success) {
                updateState(this, "groupedOrders", response.data);
                this.syncStocksFromData(response.data, "GROUPED");
            }
        }

        loadingComplete(this);
    }

    // Yeni filtre geldiğinde sayfayı 0. sayfaya döndür
    async onQuery(query: any) {
        this.setState({ query: query.query, page: 0 }, () => {
            this.loadOrders(query.query, 0);
        });
    }

    updateGroupedOrdersState(updatedOrder: any) {
        const updatedOrdersList = Array.isArray(updatedOrder) ? updatedOrder : [updatedOrder];

        const newGroupedOrders = this.state.groupedOrders.map((buyer: any) => {
            return {
                ...buyer,
                warehouses: buyer.warehouses.map((wh: any) => {
                    return {
                        ...wh,
                        orders: wh.orders.map((order: any) => {
                            const foundUpdatedOrder = updatedOrdersList.find((u: any) => u.id === order.id);
                            return foundUpdatedOrder ? foundUpdatedOrder : order;
                        })
                    };
                })
            };
        });
        this.setState({ groupedOrders: newGroupedOrders });
    }

    // ============================================================
    // 3. AKSİYONLAR
    // ============================================================
    async onOrderItemAction(actionType: any, orderId: number, orderItemId: number, partialQuantity: number | null) {
        loadingStart(this);
        let response = null;

        try {
            if ("cancel" == actionType) {
                response = await OrderApi.cancelOrderItem(orderId, orderItemId);
            }
            else if ("approve" == actionType) {
                response = await OrderApi.approveOrderItem(orderId, orderItemId);
            }
            else if ("approvePartially" == actionType) {
                response = await OrderApi.approveOrderItemPartially(orderId, orderItemId, partialQuantity || 0);
                if (response && response.success) {
                    updateState(this, "showApprovePartiallyModal", false);
                    updateState(this, "selectedOrderItem", {});
                }
            }
            else if (OrderItemAction.TAKE_BACK_FROM_WAREHOUSE == actionType || actionType === "TAKE_BACK_FROM_WAREHOUSE") {
                response = await OrderApi.takeBackFromWareHouse(orderId, orderItemId);
            }

            if (response && response.success) {
                const processedOrder = response.data.processedOrder || response.data;

                if (processedOrder) {
                    upsertStateIn(this, "orders", processedOrder);
                    if (this.state.viewType === "GROUPED") {
                        this.updateGroupedOrdersState(processedOrder);
                    }

                    if (processedOrder.orderItems && Array.isArray(processedOrder.orderItems)) {
                        let newStockUpdates: any = {};
                        processedOrder.orderItems.forEach((item: any) => {
                            if (item.productId && item.liveStockInfo) {
                                newStockUpdates[item.productId] = item.liveStockInfo;
                            }
                        });
                        if (Object.keys(newStockUpdates).length > 0) {
                            this.mergeStockUpdates(newStockUpdates);
                        }
                    }
                }
            }
        } catch (e) {
            console.error("🔥 HATA:", e);
        } finally {
            loadingComplete(this);
        }
    }

    async onOrderAction(action: OrderAction, orderId: number) {
        loadingStart(this);
        let response = await OrderActionApi.onAction(action, orderId);

        if (response.success) {
            const data = response.data;
            const processedOrder = data.processedOrder || (data.id ? data : null);

            if (processedOrder) {
                upsertStateIn(this, "orders", processedOrder);
                if (this.state.viewType === "GROUPED") {
                    this.updateGroupedOrdersState(processedOrder);
                }

                if (processedOrder.orderItems && Array.isArray(processedOrder.orderItems)) {
                    let newStockUpdates: any = {};
                    processedOrder.orderItems.forEach((item: any) => {
                        if (item.productId && item.liveStockInfo) {
                            newStockUpdates[item.productId] = item.liveStockInfo;
                        }
                    });
                    if (Object.keys(newStockUpdates).length > 0) {
                        setTimeout(() => {
                             this.mergeStockUpdates(newStockUpdates);
                        }, 0);
                    }
                }
            }
        }
        loadingComplete(this);
    }

    // ============================================================
    // 4. MODAL VE RENDER
    // ============================================================
    showApprovePartiallyModal(orderId: number, orderItemId: number, orderItem: any) {
        updateState(this, "selectedOrderId", orderId);
        updateState(this, "selectedOrderItemId", orderItemId);
        updateState(this, "selectedOrderItem", orderItem);
        updateState(this, "showApprovePartiallyModal", true);
    }

    onParciallyApprove(e: any) {
        e.preventDefault();
        let enteredQuantity = parseFloat(e.target.partialQuantity.value);
        const orderId = Number(e.target.orderId.value);
        const orderItemId = Number(e.target.orderItemId.value);
        this.onOrderItemAction("approvePartially", orderId, orderItemId, enteredQuantity);
    }

    renderOrders = () => {
        return (
            <Accordion defaultActiveKey="0" style={{ marginTop: "10px" }}>
                {this.state.orders && this.state.orders.map((item: any) => (
                    <OrderAccordionItem
                        key={item.id}
                        order={item}
                        viewType="FLAT"
                        liveStockMap={this.state.productLiveStockMap}
                        onOrderAction={(act: any, id: number) => this.onOrderAction(act, id)}
                        onOrderItemAction={(type: any, oid: number, iid: number, qty: number | null) => this.onOrderItemAction(type, oid, iid, qty)}
                        onShowPartialModal={(oid: number, iid: number, itm: any) => this.showApprovePartiallyModal(oid, iid, itm)}
                    />
                ))}
            </Accordion>
        );
    };

    renderGroupedOrders() {
        const { groupedOrders } = this.state;
        if (!groupedOrders?.length) return <div>Veri bulunamadı</div>;

        return (
            <Accordion defaultActiveKey="0" alwaysOpen style={{ marginTop: "10px" }}>
                {groupedOrders.map((buyer: any, bIndex: number) => (
                    <Accordion.Item eventKey={`buyer-${bIndex}`} key={buyer.buyerId} style={{ marginBottom: "5px" }}>
                        <Accordion.Header><strong>{buyer.buyerTitle}</strong></Accordion.Header>
                        <Accordion.Body style={{ padding: "10px" }}>
                            <Accordion alwaysOpen>
                                {buyer.warehouses.map((wh: any, wIndex: number) => (
                                    <Accordion.Item eventKey={`wh-${bIndex}-${wIndex}`} key={wh.warehouseId} style={{ marginBottom: "5px", border: "none" }}>
                                        <Accordion.Header style={{ display: 'flex', alignItems: 'center', backgroundColor: "#f8f9fa" }}>
                                            <span style={{ fontSize: "0.9rem", color: "#555" }}>📍 Depo: {wh.warehouseTitle}</span>
                                        </Accordion.Header>
                                        <Accordion.Body style={{ padding: "5px 0 0 0" }}>
                                            <Accordion>
                                                {wh.orders.map((order: any) => (
                                                    <OrderAccordionItem
                                                        key={order.id}
                                                        order={order}
                                                        viewType="GROUPED"
                                                        liveStockMap={this.state.productLiveStockMap}
                                                        onOrderAction={(act: any, id: number) => this.onOrderAction(act, id)}
                                                        onOrderItemAction={(type: any, oid: number, iid: number, qty: number | null) => this.onOrderItemAction(type, oid, iid, qty)}
                                                        onShowPartialModal={(oid: number, iid: number, itm: any) => this.showApprovePartiallyModal(oid, iid, itm)}
                                                    />
                                                ))}
                                            </Accordion>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        </Accordion.Body>
                    </Accordion.Item>
                ))}
            </Accordion>
        );
    }

    render() {
        return (
            <AuthorizedPage role={Role.ORDER_MANAGER} isLoading={this.state.isLoading}>
                <div className="centered-99-percent">

                    <div style={{ marginBottom: "10px" }} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                            <Button
                                size="sm"
                                variant={this.state.viewType === "FLAT" ? "primary" : "outline-primary"}
                                onClick={() => this.setState({ viewType: "FLAT", page: 0 }, () => this.loadOrders(this.state.query, 0))}
                            >
                                Sipariş Bazlı
                            </Button>

                            <Button
                                size="sm"
                                style={{ marginLeft: "10px" }}
                                variant={this.state.viewType === "GROUPED" ? "primary" : "outline-primary"}
                                onClick={() => this.setState({ viewType: "GROUPED" }, () => this.loadOrders(this.state.query))}
                            >
                                Müşteri Bazlı
                            </Button>
                        </div>

                        <Dropdown align="end">
                            <Dropdown.Toggle size="sm" variant="outline-secondary">
                                Gelişmiş İşlemler
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => this.setState({ showLogoXmlImportModal: true })}>
                                    Logo XML Aktar
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => this.setState({ showOrderDeleteModal: true })}
                                >
                                    Sipariş Sil (geçici)
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>

                    <OrderQueryFilter onQuery={(query: any) => this.onQuery(query)} />

                    {/* Verileri Listele */}
                    {this.state.viewType === "GROUPED" ? this.renderGroupedOrders() : this.renderOrders()}

                    {/* 🔥 SAYFALAMA BİLEŞENİ (Sadece FLAT görünümde ve sayfa sayısı 1'den büyükse göster) */}
                    {this.state.viewType === "FLAT" && this.state.totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-4 mb-5">
                            <Pagination>
                                <Pagination.Prev
                                    disabled={this.state.page === 0}
                                    onClick={() => this.handlePageChange(this.state.page - 1)}
                                />

                                {/* 0'dan Toplam Sayfaya kadar döngü kurup sayfa numarası basıyoruz */}
                                {[...Array(this.state.totalPages)].map((_, index) => (
                                    <Pagination.Item
                                        key={index}
                                        active={index === this.state.page}
                                        onClick={() => this.handlePageChange(index)}
                                    >
                                        {index + 1}
                                    </Pagination.Item>
                                ))}

                                <Pagination.Next
                                    disabled={this.state.page === this.state.totalPages - 1}
                                    onClick={() => this.handlePageChange(this.state.page + 1)}
                                />
                            </Pagination>
                        </div>
                    )}

                    <PartiallyApproveModal
                        show={this.state.showApprovePartiallyModal}
                        onHide={() => updateState(this, "showApprovePartiallyModal", false)}
                        selectedOrderItem={this.state.selectedOrderItem}
                        selectedOrderId={this.state.selectedOrderId}
                        selectedOrderItemId={this.state.selectedOrderItemId}
                        onApprove={this.onParciallyApprove}
                    />

                    <LogoXmlImportModal
                        show={this.state.showLogoXmlImportModal}
                        onHide={() => this.setState({ showLogoXmlImportModal: false })}
                        onImported={() => this.loadOrders(this.state.query)}
                    />

                    <OrderDeleteModal
                        show={this.state.showOrderDeleteModal}
                        onHide={() => this.setState({ showOrderDeleteModal: false })}
                        orders={this.state.orders}
                        onDeleted={() => this.loadOrders(this.state.query)}
                    />

                </div>
            </AuthorizedPage>
        );
    }
}

export default AdminOrderManagement;